import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './login.css';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { getProfile, postLogin } from '../../services/requests';
import { Profile } from '../../interfaces/interfaces';
import { ClipLoader } from "react-spinners";
import Logo4 from '../../components/image/logoLogin.svg';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loadingBtn, setLoadingBtn] = useState(false);

    /**
     * Maneja el envío del formulario de login.
     * - Envía las credenciales al backend.
     * - Si son válidas, guarda el token, el usuario, y otros datos en cookies.
     * - Recupera información de perfil del usuario.
     * - Según el rol, redirige a una ruta específica.
     */
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        setLoadingBtn(true);

        try {
            const responseLogin = await postLogin(email, password);

            if (responseLogin.status === 200 && responseLogin.data.token) {
                const time = 1 / 24;
                Cookies.set('token', responseLogin.data.token, { expires: time });
                Cookies.set('username', `${responseLogin.data.user.username} ${responseLogin.data.user.last_name}`, { expires: time });
                Cookies.set('name', `${responseLogin.data.user.first_name} ${responseLogin.data.user.last_name}`, { expires: time });
                Cookies.set('email', `${responseLogin.data.user.email}`, { expires: time });
                Cookies.set('rotate-phone', `1`, { expires: time });
                Cookies.set('start-info', `1`, { expires: time });

                const responseProfile = await getProfile(responseLogin.data.token);

                if (responseProfile.status === 200) {

                    let role;
                    let roleName;
                    const userData = (responseProfile.data.data as Profile[]).find(item => item?.user?.username === responseLogin.data.user.username);

                    if (userData!.Rol === "SAT") {
                        role = "admin"
                        roleName = "SAT"
                    } else if (userData!.Rol === "Adm") {
                        role = "admin"
                        roleName = "admin"
                    } else if (userData!.Rol === "Gtr") {
                        role = "manager"
                        roleName = "manager"
                    } else if (userData!.Rol === "Emb") {
                        role = "costumer"
                        roleName = "costumer"
                    }
                    Cookies.set('idUser', userData?.id, { expires: time });
                    Cookies.set('phone', `${userData?.Tcontacto}`, { expires: time });
                    Cookies.set('role', role, { expires: time });
                    Cookies.set('roleName', roleName, { expires: time });

                    const saveCompanyAndVessel = (data: any[], username: string, role: string) => {
                        const userData = data.find(item => item?.user?.username === username);
                        if (userData) {
                            const Nempresa = userData.Nempresa?.Nempresa ?? "";
                            const Nembarcacion = userData.Nembarcacion?.Nembarcacion ?? "";

                            if (role === "admin") {
                                Cookies.set("Nempresa", "", { expires: time });
                                Cookies.set("Nembarcacion", "", { expires: time });
                            } else if (role === "manager") {
                                Cookies.set("Nempresa", Nempresa.toString(), { expires: time });
                                Cookies.set("Nembarcacion", "", { expires: time });
                            } else if (role === "costumer") {
                                Cookies.set("Nempresa", Nempresa.toString(), { expires: time });
                                Cookies.set("Nembarcacion", Nembarcacion.toString(), { expires: time });
                            }
                        } else {
                            console.log("Usuario no encontrado en la data.");
                        }
                    };

                    if (role && responseLogin.data?.user?.username) {
                        saveCompanyAndVessel(responseProfile.data.data, responseLogin.data.user.username, role);
                    } else {
                        console.log("El role o el username es undefined");
                    }

                    setLoadingBtn(false);

                    if (role === "admin") {
                        navigate('/SmartPort/Home');
                    } else if (role === "costumer" || role === "manager") {
                        navigate('/SmartPort/Company');
                    }

                } else {
                    setLoadingBtn(false);
                    setError('Token Invalido');
                }

            } else {
                setLoadingBtn(false);
                setError('Credenciales inválidas');
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || error.response?.data?.detail || error.message || error;
            setLoadingBtn(false);
            console.error(error);
            setError(`${errorMessage}`);
        }
    };

    /**
     * Al montar el componente, carga las credenciales guardadas en cookies si existen.
     * Esto se usa para mantener el formulario lleno si el usuario marcó "Recordar".
     */
    useEffect(() => {
        const savedEmail = Cookies.get('email');
        const savedPassword = Cookies.get('password');

        if (savedEmail && savedPassword) {
            setEmail(savedEmail);
            setPassword(savedPassword);
            setRememberMe(true);
        }
    }, []);

    /**
     * Guarda o elimina las credenciales del usuario en cookies según la opción de "Recordarme".
     * - Si está activado, guarda email y contraseña.
     * - Si no, las elimina.
     */
    useEffect(() => {
        if (rememberMe) {
            const time = 1 / 24;
            Cookies.set('email', email, { expires: time });
            Cookies.set('password', password, { expires: time });
        } else {
            Cookies.remove('email');
            Cookies.remove('password');
        }
    }, [rememberMe, email, password]);

    return (
        <div className="login-container">
            <div className='titleMain'>
                <img src={Logo4} alt="Monaguillos IoT" className='logo2-image' />
            </div>

            <div className="login-card">
                <div className='title'>
                    <h2>{t('login.signIn')}</h2>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('login.email')}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t('login.password')}
                            required
                        />
                    </div>

                    <div className="rememberPassword">
                        <input type="checkbox" id="rememberMe" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                        <label htmlFor="rememberMe">{t('login.rememberMe')}</label>
                    </div>

                    <div className="options">
                        {/* <a href="#" className="forgot-password">Forgot password?</a> */}
                        <button type="submit" className="btn" disabled={loadingBtn}>
                            {loadingBtn ? (
                                <div className="loading-btn">
                                    <ClipLoader color="#007bff" size={20} />
                                </div>
                            ) : (
                                t('login.signIn')
                            )}
                        </button>
                    </div>

                    {error && <p className="text-danger">{error}</p>}
                </form>
            </div>

            <div className='footer'>
                <a href="/pdfs/CondicionesDeUso.pdf" target="_blank" rel="noopener noreferrer">
                    {t('login.termsOfUse')}
                </a>
                <a href="/pdfs/PoliticaDePrivacidad.pdf" target="_blank" rel="noopener noreferrer">
                    {t('login.privacyPolicy')}
                </a>
            </div>
        </div>
    );
};

export default Login;
